import { useRef, useState } from "react";
import type { Entity } from "@kkindex/shared";
import type { Execute } from "./Editor";
import { useSelection } from "./interaction";
import s from "./App.module.css";

// Keep the same input mounted across both clicks so a double-click can open details.
export function InlineTitle({
  item,
  selectionKey,
  execute,
  edit,
  className = "",
  readonly = false,
  titleTooltip = false,
}: {
  item: Entity;
  selectionKey: string;
  execute: Execute;
  edit: (item: Entity) => void;
  className?: string;
  readonly?: boolean;
  titleTooltip?: boolean;
}) {
  const { select } = useSelection();
  const [draft, setDraft] = useState<string | null>(null);
  const session = useRef<{
    title: string;
    original: string;
    revision: number;
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const begin = (input: HTMLInputElement) => {
    select(selectionKey);
    if (readonly || busy || session.current) return;
    session.current = {
      title: item.title,
      original: item.title,
      revision: item.revision,
    };
    setDraft(item.title);
    input.focus();
  };
  const finish = async (open = false) => {
    const current = session.current;
    session.current = null;
    setDraft(null);
    let latest = item;
    const title = current?.title.trim();
    if (current && title && title !== current.original) {
      setBusy(true);
      try {
        const response = (await execute(`/items/${item.id}`, "PATCH", {
          title,
          revision: current.revision,
        })) as { item: Entity };
        latest = response.item;
      } catch {
        return;
      } finally {
        setBusy(false);
      }
    }
    if (open) edit(latest);
  };
  return (
    <span className={`${s.titleSlot} ${className}`}>
      <input
        className={s.inlineTitle}
        size={Math.max(
          1,
          [...item.title].reduce(
            (n, char) => n + (char.charCodeAt(0) > 255 ? 2 : 1),
            0,
          ),
        )}
        data-inline-title="true"
        data-editing={draft !== null}
        aria-label={`标题：${item.title}`}
        title={
          readonly || titleTooltip
            ? item.title
            : "单击改名，点击别处保存；双击打开详情"
        }
        value={draft ?? item.title}
        readOnly={draft === null}
        aria-busy={busy}
        maxLength={200}
        onClick={(event) => {
          event.stopPropagation();
          begin(event.currentTarget);
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (busy || item.archived || (readonly && !item.builtin)) return;
          void finish(true);
          event.currentTarget.blur();
        }}
        onChange={(event) => {
          if (!session.current) return;
          session.current.title = event.target.value;
          setDraft(event.target.value);
        }}
        onBlur={() => void finish()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.nativeEvent.isComposing) return;
          if (event.key === "F2") {
            event.preventDefault();
            begin(event.currentTarget);
          }
          if (event.key === "Enter") {
            event.preventDefault();
            event.currentTarget.blur();
          }
          if (event.key === "Escape") {
            event.preventDefault();
            session.current = null;
            setDraft(null);
            event.currentTarget.blur();
          }
        }}
      />
    </span>
  );
}
